import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Divider,
  FormHelperText,
  Autocomplete,
  Tooltip,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { getCacheUsers, getUser, putUser, putCacheUser } from "../API/userAPI";
import { getCacheTechnicians } from "../API/technicianAPI";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { Add as AddIcon } from "@mui/icons-material";
import { Delete as DeleteIcon } from "@mui/icons-material";

import { userValidationUpdateSchema } from "./utils/userValidationSchema";
import BaseWrapper from "../../components/shared/BaseWrapper";

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function ManageUsersComponent() {
  const navigate = useNavigate();
  const [usersCache, setUsersCache] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [user, setUser] = useState({
    firstname: "",
    middle_initial: "",
    lastname: "",
    username: "",
    email: "",
    password: "",
    confirm_password: "",
    date_hired: "",
    contact: [{ type: "", country_code: "", number: "", description: "" }],
    roles: [],
  });
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [hasNameChanged, setHasNameChanged] = useState(false);
  const [hasUsernameChanged, setHasUsernameChanged] = useState(false);
  const [hasEmailChanged, setHasEmailChanged] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });
  const [newPassword, setNewPassword] = useState("");
  const [techniciansCache, setTechniciansCache] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);

  useEffect(() => {
    const fetchCacheUsers = async () => {
      try {
        const usersCache = await getCacheUsers();
        setUsersCache(usersCache || []);
      } catch (err) {
        setError("Failed to fetch users cache");
      }
    };

    fetchCacheUsers();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setTechniciansLoading(true);
        const techs = await getCacheTechnicians(); // [{id, name}, ...]
        if (mounted) setTechniciansCache(Array.isArray(techs) ? techs : []);
      } finally {
        if (mounted) setTechniciansLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSearchUser = (event, value) => {
    setIsFormVisible(false);
    setSelectedUser(value || null);
  };

  const handleSearchButtonClick = async () => {
    if (!selectedUser) {
      console.log("No user selected");
      return;
    }

    if (isFetchingUser) {
      console.log("Already fetching user details...");
      return;
    }

    setIsFetchingUser(true);
    setIsLoading(true);

    try {
      const fetchedUser = await getUser(selectedUser.id);
      setUser(fetchedUser);
      setIsFormVisible(true);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setIsFetchingUser(false);
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsFormVisible(false); // Hide the form
    setIsEdit(false);        // Ensure edit mode is reset
  };

  const handleUserChange = (field, value) => {
    setUser((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "firstname" || field === "lastname") {
      setHasNameChanged(true);
    }

    if (field === "username") {
      setHasUsernameChanged(true);
    }

    if (field === "email") {
      setHasEmailChanged(true);
    }
  };

  const handleViewEditButtonClick = () => {
    setIsEdit((prevIsEdit) => !prevIsEdit);
  };

  const handleRoleChange = (e) => {
    const { value, checked } = e.target;

    setUser((prevUser) => {
      let updatedRoles;

      if (checked) {
        updatedRoles = [...prevUser.roles, value];
      } else {
        updatedRoles = prevUser.roles.filter((role) => role !== value);
      }
      
      // if operations_technician was unchecked, clear technician_id
      const clearingTechId =
        value === "operations_technician" && !checked ? { technician_id: "" } : {};

      return {
        ...prevUser,
        roles: updatedRoles,
        ...clearingTechId,
      };
    });
  };

  const handleContactChange = (index, field, value) => {
    const updatedContact = user.contact.map((contact, i) =>
      i === index
        ? {
          ...contact,
          [field]: field === "type" ? value.toLowerCase() : value,
        }
        : contact
    );
    setUser({
      ...user,
      contact: updatedContact,
    });
  };

  const addContactField = () => {
    setUser({
      ...user,
      contact: [
        ...user.contact,
        { type: "", country_code: "", number: "", description: "" },
      ],
    });
  };

  const removeContactField = (index) => {
    if (user.contact.length > 1) {
      const updatedContact = user.contact.filter((_, i) => i !== index);
      setUser({
        ...user,
        contact: updatedContact,
      });
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    const { timestamp_create, timestamp_update, ...updatedUserParameters } =
      user;

    if (newPassword) {
      updatedUserParameters.password = newPassword;
    }

    console.log("User object:", user);
    console.log("Updated user parameters:", updatedUserParameters);

    console.log(usersCache);
    try {
      await userValidationUpdateSchema(user.id, usersCache).validate(
        updatedUserParameters,
        { abortEarly: false }
      );

      setFormError({});
      setErrorMessage("");
      setIsLoading(true);

      if (user.roles.includes("operations_technician")) {
        // must carry a technician_id
        if (!user.technician_id) {
          setFormError((prev) => ({ ...prev, technician_id: "Please select a technician." }));
          setIsLoading(false);
          return;
        }
      } else {
        // clear if not an ops tech
        if (updatedUserParameters.hasOwnProperty("technician_id") || user.technician_id) {
          updatedUserParameters.technician_id = "";
        }
      }

      const response = await putUser(user.id, updatedUserParameters);
      console.log("User update response:", response);

      if (response) {
        let cacheUpdateSuccess = true;

        const cacheUserParameters = {};
        let shouldUpdateCache = false;

        if (hasNameChanged) {
          cacheUserParameters.name = `${user.lastname}, ${user.firstname}`;
          shouldUpdateCache = true;
        }

        if (hasUsernameChanged) {
          cacheUserParameters.username = user.username;
          shouldUpdateCache = true;
        }

        if (hasEmailChanged) {
          cacheUserParameters.email = user.email;
          shouldUpdateCache = true;
        }

        if (shouldUpdateCache) {
          const cacheResponse = await putCacheUser(
            user.id,
            cacheUserParameters
          );
          console.log("Cache user update response:", cacheResponse);

          if (cacheResponse) {
            setUsersCache((prevCache) =>
              prevCache.map((usr) =>
                usr.id === user.id ? { ...usr, ...cacheUserParameters } : usr
              )
            );

            setSelectedUser((prevSelected) =>
              prevSelected?.id === user.id
                ? { ...prevSelected, ...cacheUserParameters }
                : prevSelected
            );

            setHasNameChanged(false);
          } else {
            cacheUpdateSuccess = false;
            setErrorMessage(
              "An unexpected error occurred while updating the cache. Please try again later."
            );
          }
        }

        if (cacheUpdateSuccess) {
          setSuccessMessage("User updated successfully!");
        }
      } else {
        setErrorMessage(
          "An unexpected error occurred while updating the User. Please try again later."
        );
      }
    } catch (validationErrors) {
      if (validationErrors.inner) {
        const formattedErrors = validationErrors.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});

        setFormError(formattedErrors);
      } else {
        setErrorMessage(
          validationErrors.message ||
          "Validation failed. Please check your input and try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <BaseWrapper
      titleIcon={<AdminPanelSettingsIcon />}
      title="Admin Hub"
      subTitle="Manage Users"
      subTitleRoute={"/manage-users"}
      createButtonLabel="Create User"
      onClickCreateButton={() => navigate("/create-user")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid item xs={12}>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Autocomplete
              freeSolo
              options={usersCache}
              getOptionLabel={(user) => user?.name || ""}
              value={selectedUser}
              onChange={(event, newValue) => handleSearchUser(event, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Name"
                  variant="outlined"
                  fullWidth
                  error={!!error}
                  helperText={error || ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} container alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchButtonClick}
              disabled={isLoading}
            >
              {isLoading ? "Searching..." : "Search"}{" "}
            </Button>
            {isFormVisible && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewEditButtonClick}
                sx={{ ml: 2 }}
                disabled={isLoading}
              >
                {isEdit ? "Lock Record" : "Edit Record"}{" "}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      <StyledHr />

      {isFormVisible && user && (
        <form
          onSubmit={handleSubmit}
          className="user-form"
          autoComplete="off"
          noValidate
        >
          <Grid container spacing={3}>
            <Grid item>
              <Card
                variant="outlined"
                sx={{ padding: "20px", marginBottom: "20px" }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      sx={{
                        paddingBottom: "1rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "1rem",
                      }}
                    >
                      Basic Information
                    </Typography>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextField
                        label="First Name"
                        id="firstname"
                        name="firstname"
                        value={user.firstname}
                        onChange={(e) =>
                          handleUserChange("firstname", e.target.value)
                        }
                        size="small"
                        required
                        fullWidth
                        error={isEdit && !!error?.firstname}
                        helperText={isEdit && formError?.firstname}
                        disabled={!isEdit || isLoading}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Middle Initial"
                        id="middle_initial"
                        name="middle_initial"
                        value={user.middle_initial}
                        onChange={(e) =>
                          handleUserChange("middle_initial", e.target.value)
                        }
                        size="small"
                        fullWidth
                        disabled={!isEdit || isLoading}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Last Name"
                        id="lastname"
                        name="lastname"
                        value={user.lastname}
                        onChange={(e) =>
                          handleUserChange("lastname", e.target.value)
                        }
                        size="small"
                        required
                        fullWidth
                        error={isEdit && !!error?.lastname}
                        helperText={isEdit && formError?.lastname}
                        disabled={!isEdit || isLoading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Username"
                        id="username"
                        name="username"
                        value={user.username}
                        onChange={(e) =>
                          handleUserChange("username", e.target.value)
                        }
                        size="small"
                        required
                        fullWidth
                        error={isEdit && !!error?.username}
                        helperText={isEdit && formError?.username}
                        disabled={!isEdit || isLoading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Email"
                        id="email"
                        name="email"
                        type="email"
                        value={user.email}
                        onChange={(e) =>
                          handleUserChange("email", e.target.value)
                        }
                        size="small"
                        required
                        fullWidth
                        error={isEdit && !!error?.email}
                        helperText={isEdit && formError?.email}
                        disabled={!isEdit || isLoading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Date Hired"
                        id="date_hired"
                        name="date_hired"
                        type="date"
                        value={user.date_hired}
                        onChange={(e) =>
                          handleUserChange("date_hired", e.target.value)
                        }
                        size="small"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled={!isEdit || isLoading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Link to={`/change-password/${user.id}`}>
                        Change Password
                      </Link>
                    </Grid>
                      {/* <Grid item xs={6}>
                      <TextField
                        label="Change Password (optional)"
                        type={showPasswords.password ? "text" : "password"}
                        id="password"
                        name="password"
                        value={isEdit ? newPassword : user.password}
                        onChange={(e) => setNewPassword(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  handleClickShowPassword("password")
                                }
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPasswords.password ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        disabled={!isEdit || isLoading}
                      />
                    </Grid> */}
                    </Grid>
                    <Grid item xs={12}>
                      <Divider
                        variant="fullWidth"
                        sx={{
                          my: 4,
                          borderColor: "rgba(0, 0, 0, 0.12)",
                          borderWidth: "0.5px",
                        }}
                      />
                    </Grid>
                    <Grid container>
                      <Grid item xs={12}>
                        <CardHeader
                          title={
                            <Box
                              display="flex"
                              alignItems="center"
                              width="100%"
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: "bold",
                                  textTransform: "uppercase",
                                  fontSize: "1rem",
                                }}
                                mr={2}
                              >
                                Contact Information (optional)
                              </Typography>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={addContactField}
                                size="small"
                                disabled={!isEdit || isLoading}
                              >
                                Add Another Contact Person
                              </Button>
                            </Box>
                          }
                          sx={{ padding: "16px" }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CardContent sx={{ padding: "20px" }}>
                          <Grid container spacing={2}>
                            {user.contact.map((contact, index) => (
                              <React.Fragment key={index}>
                                <Grid item xs={2}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel id={`contact-type-${index}`}>
                                      Type
                                    </InputLabel>
                                    <Select
                                      labelId={`contact-type-${index}`}
                                      value={contact.type}
                                      onChange={(e) =>
                                        handleContactChange(
                                          index,
                                          "type",
                                          e.target.value
                                        )
                                      }
                                      label="Type"
                                      size="small"
                                      disabled={!isEdit || isLoading}
                                    >
                                      <MenuItem value="mobile">Mobile</MenuItem>
                                      <MenuItem value="landline">
                                        Landline
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel id={`country-code-${index}`}>
                                      Country Code
                                    </InputLabel>
                                    <Select
                                      labelId={`country-code-${index}`}
                                      value={contact.country_code}
                                      onChange={(e) =>
                                        handleContactChange(
                                          index,
                                          "country_code",
                                          e.target.value
                                        )
                                      }
                                      label="Country Code"
                                      size="small"
                                      disabled={!isEdit || isLoading}
                                    >
                                      <MenuItem value="+63">+63</MenuItem>
                                      <MenuItem value="+1">+1</MenuItem>
                                      <MenuItem value="+44">+44</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    label="Contact Number"
                                    id={`contact_number_${index}`}
                                    type="text"
                                    value={contact.number}
                                    onChange={(e) =>
                                      handleContactChange(
                                        index,
                                        "number",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    fullWidth
                                    error={
                                      isEdit &&
                                      !!error?.contact?.[index]?.number
                                    }
                                    helperText={
                                      isEdit &&
                                      formError?.contact?.[index]?.number
                                    }
                                    disabled={!isEdit || isLoading}
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <TextField
                                    label="Description"
                                    id={`contact_description_${index}`}
                                    value={contact.description}
                                    onChange={(e) =>
                                      handleContactChange(
                                        index,
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    fullWidth
                                    disabled={!isEdit || isLoading}
                                  />
                                </Grid>
                                <Grid item xs>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    height="100%"
                                  >
                                    <Tooltip title="Delete">
                                      <span>
                                        <IconButton
                                          onClick={() =>
                                            removeContactField(index)
                                          }
                                          disabled={
                                            !isEdit ||
                                            isLoading ||
                                            user.contact.length === 1
                                          }
                                          sx={{
                                            color:
                                              !isEdit ||
                                                isLoading ||
                                                user.contact.length === 1
                                                ? "rgba(0, 0, 0, 0.26)"
                                                : "#DC3545",
                                            cursor:
                                              !isEdit ||
                                                isLoading ||
                                                user.contact.length === 1
                                                ? "default"
                                                : "pointer",
                                          }}
                                        >
                                          <DeleteIcon
                                            sx={{
                                              fontSize: "1.5rem",
                                            }}
                                          />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                              </React.Fragment>
                            ))}
                          </Grid>
                        </CardContent>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider
                        variant="fullWidth"
                        sx={{
                          my: 4,
                          borderColor: "rgba(0, 0, 0, 0.12)",
                          borderWidth: "0.5px",
                        }}
                      />
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <CardHeader
                          title={
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                fontSize: "1rem",
                              }}
                            >
                              User Roles *
                            </Typography>
                          }
                          sx={{ padding: "16px" }}
                        />
                        <CardContent sx={{ padding: "20px" }}>
                          <Grid container spacing={3}>
                            <Grid item xs={6}>
                              <FormGroup>
                                {[
                                  "sales",
                                  "operations_admin",
                                  "operations_coordinator",
                                  "operations_technician",
                                ].map((role, index) => (
                                  <FormControlLabel
                                    key={index}
                                    control={
                                      <Checkbox
                                        id={role}
                                        name="roles"
                                        value={role}
                                        checked={user.roles.includes(role)}
                                        onChange={handleRoleChange}
                                        disabled={!isEdit || isLoading}
                                      />
                                    }
                                    label={role
                                      .replace(/_/g, " ")
                                      .split(" ")
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1)
                                      )
                                      .join(" ")}
                                  />
                                ))}
                              </FormGroup>
                            </Grid>
                            <Grid item xs={6}>
                              <FormGroup>
                                {[
                                  "finance",
                                  "account_manager",
                                  "office_admin",
                                  "customer_service",
                                  "system_admin",
                                ].map((role, index) => (
                                  <FormControlLabel
                                    key={index}
                                    control={
                                      <Checkbox
                                        id={role}
                                        name="roles"
                                        value={role}
                                        checked={user.roles.includes(role)}
                                        onChange={handleRoleChange}
                                        disabled={!isEdit || isLoading}
                                      />
                                    }
                                    label={role
                                      .replace(/_/g, " ")
                                      .split(" ")
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1)
                                      )
                                      .join(" ")}
                                  />
                                ))}
                              </FormGroup>
                            </Grid>
                            {isEdit && formError.roles && (
                              <Grid item xs={12}>
                                <FormHelperText error>
                                  {formError.roles}
                                </FormHelperText>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Grid>
                    </Grid>
                    {/* Technician link — only when Operations Technician role is selected */}
                    {user.roles.includes("operations_technician") && (
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                          <Autocomplete
                            options={techniciansCache}
                            getOptionLabel={(t) => t?.name || ""}
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            loading={techniciansLoading}
                            value={
                              techniciansCache.find((t) => t.id === user.technician_id) || null
                            }
                            onChange={(_, newValue) =>
                              handleUserChange("technician_id", newValue?.id || "")
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Link to Technician"
                                placeholder="Search technician…"
                                helperText="Required for Operations Technician role"
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                  <span>{option.name}</span>
                                  <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                                </div>
                              </li>
                            )}
                            // 👇 disable Autocomplete when record is locked
                            disabled={!isEdit || isLoading}
                          />
                        </FormControl>
                      </Grid>
                    )}


                    {isEdit && (
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        mt={2}
                        mb={2}
                        mr={2}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleCancel}
                          disabled={isLoading}
                          sx={{
                            fontSize: "0.875rem",
                            padding: "8px 16px",
                            color: "#FF4C4C",
                            borderColor: "#FF4C4C",
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={isLoading}
                          sx={{
                            marginLeft: "10px",
                            fontSize: "0.875rem",
                            padding: "8px 16px",
                          }}
                        >
                          Submit
                        </Button>
                      </Grid>
                    )}

                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      )}
    </BaseWrapper>
  );
}

export default ManageUsersComponent;
